/* eslint-disable react-compiler/react-compiler */
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import * as React from 'react'
import { toast } from 'sonner'
import { isHotkey } from 'is-hotkey'
import {
  type Block,
  type ComponentBlock,
  type InstanceBlock,
  type ReferenceBlock,
  blockTypesRecord,
  isInstanceBlock,
  isComponentBlock,
  type BlockWithChildren,
  getBlockUpdateOverride,
  getValueBasedOnSelectedVariants,
  normalizeStringPath,
} from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import { invariant } from '@epic-web/invariant'
import { getId } from '@basehub/utils/id/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { BlockIcon } from '~/components/block-icon'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useTabsStore } from '../../tabs/store'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import {
  compareInstanceSearchQueueItem,
  findAcceptedTypeOptionForBlock,
} from '@basehub/data/replicache/schema/block/reference/util'
import Selector, {
  SelectorEmptyState,
  SelectorItem,
} from '~/sotano-kit/primitives/selector'
import Link from 'next/link'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useEditorStore } from '../../editor-store'
import { useRepoStore } from '../../repo-store'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { useBranchOrCommitSegment } from '~/lib/commits/use-selected-branch-or-commit'
import { CreateAndReferenceBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/reference'
import { useEditorMode } from '../../editor-mode-provider'
import { Blocks } from '..'
import { createStore, useStore } from 'zustand'
import { Portal } from '~/components/portal'
import { insertIntoArray } from '@basehub/utils/array/insert-into-array'
import { ChildBlocksExpanded, useExpandedState } from '../document/nested/link'
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ComponentNoneIcon,
} from '@radix-ui/react-icons'
import { BlockLabelMoreActionsPortal } from '../block-wrapper'
import { CollapseIcon, ExpandIcon } from '~/components/icons/expand-collapse'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import {
  VALID_IMAGE_MIME_TYPES,
  VALID_VIDEO_MIME_TYPES,
} from '@basehub/data/replicache/schema/block/valid-mime-types'
import { type MenuItem } from '~/sotano-kit/primitives/menu-actions-definitions'

import {
  useFloating,
  FloatingPortal,
  inline,
  autoUpdate,
  offset,
} from '@floating-ui/react'
import { autoPlacement } from '@floating-ui/dom'
import { Virtualizer } from '@tanstack/react-virtual'
import { TreeManager } from '@basehub/utils/tree/index'
import { getInvalidReferencedBlock } from '../helpers'
import {
  RowsMeta,
  usePossibleReferencesVirtualized,
} from './use-possible-references-virtualized'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import { SimpleDropdownMenu } from '~/sotano-kit/primitives/menu-actions-definitions'
import { useCustomSyncerSub } from '~/replicache/frontend/use-custom-syncer-sub'
import { Spinner } from '~/components/misc/spinner'

type ReferenceInputStore = {
  addNewBlockPosition: {
    id: string
    type: 'before' | 'after'
  } | null
  setAddNewBlockPosition: (
    position: {
      id: string
      type: 'before' | 'after'
    } | null
  ) => void
  onReorder: (
    position: {
      id: string
      type: 'before' | 'after'
    } | null
  ) => void
  onRemoveTarget: (id: string) => void
}

export const createReferenceInputStore = (
  initialState?: Partial<ReferenceInputStore>
) => {
  return createStore<ReferenceInputStore>()((set) => ({
    addNewBlockPosition: null,
    setAddNewBlockPosition: (position) =>
      set({ addNewBlockPosition: position }),
    onReorder: (_position) => undefined,
    onRemoveTarget: (_id) => undefined,
    ...initialState,
  }))
}

export const ReferenceInputStoreContext = React.createContext<ReturnType<
  typeof createReferenceInputStore
> | null>(null)

type ReferenceOption = {
  label: string
  value: string
  type: Block['type']
  breadcrumb?: string[]
  keywords?: string[]
  title?: Block['title']
  hidden?: ComponentBlock['hidden']
  preview?: ComponentBlock['typeOptions']['displayImage']
  targetBlockId?: InstanceBlock['typeOptions']['targetBlockId']
  isNew?: boolean
  componentIndex: number
  sourceBlockId: string
  loading?: boolean
}

export const ReferenceBlockInput = ({
  block,
  path: _path,
  parent,
  readOnly,
}: {
  block: ReferenceBlock
  path: string
  readOnly?: boolean
  parent: BlockWithChildren
}) => {
  const path = normalizeStringPath({
    path: _path,
    edgeId: block.id,
    format: 'string',
  })
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const editorMode = useEditorMode()
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const sourceBlockId = idToSourceBlockId(block)
  const isSelected = useEditorStore((s) => s.selectedBlockId === sourceBlockId)
  const setFocus = useRepoStore((s) => s.setFocus)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const addTab = useTabsStore((s) => s.addTab)
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const getPath = useWorkingTreeStore((s) => s.tree?.getPath)
  const undoManager = useRepoUndoManager()
  const focusValueOp = React.useMemo(() => {
    return getBlockFocusOp({
      block: { id: block.id, sourceBlockId: block.sourceBlockId },
      preferredTabKey: activeTabKey,
      focusType: 'block:value',
    })
  }, [block.id, block.sourceBlockId, activeTabKey])
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)
  const {
    typeOptions,
    acceptedTypeOptions,
    possibleReferenceNodes,
    componentSourceBlockIds,
    search,
    setSearch,
    scrollableElementRef,
    virtualizer,
    rowsMeta,
    instanceSearchResult,
    instancesPerComponent,
  } = usePossibleReferencesVirtualized({
    block,
    disabled: !isPopoverOpen,
  })

  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })

  const isReadOnly = readOnly || editorMode === 'read'

  const store = React.useMemo(
    () =>
      createReferenceInputStore({
        onRemoveTarget: async (id) => {
          await onRemoveTargetRef.current(id)
        },
      }),
    []
  )

  const addNewBlockPosition = useStore(store, (s) => s.addNewBlockPosition)
  const setAddNewBlockPosition = useStore(
    store,
    (s) => s.setAddNewBlockPosition
  )

  const options = React.useMemo(
    () =>
      possibleReferenceNodes.reduce<Array<ReferenceOption>>(
        (accumulator, node) => {
          let breadcrumb: string[] | undefined
          if (node.parentKey && getNode) {
            breadcrumb = []
            const parentNode = getNode(node.parentKey, {
              fallbackKey: 'sourceBlockId',
            })
            if (!parentNode) return accumulator
            const parentBlock = parentNode.value
            if (parentNode.parentKey) {
              const grandParentBlock = getNode(parentNode.parentKey, {
                fallbackKey: 'sourceBlockId',
              })?.value
              if (grandParentBlock && grandParentBlock.type !== 'root') {
                breadcrumb.push(grandParentBlock.title || 'Untitled')
              }
            }
            parentBlock.type !== 'root' &&
              breadcrumb.push(parentBlock.title || 'Untitled')
          }

          invariant(
            isComponentBlock(node.value) || isInstanceBlock(node.value),
            'Options for reference can only be instances or components'
          )

          let currentValue
          const sourceBlockId = idToSourceBlockId(node.value)

          if (isInstanceBlock(node.value)) {
            const component = getNode?.(
              node.value.typeOptions.targetBlockId
            )?.value
            if (!component) return accumulator
            invariant(
              component && isComponentBlock(component),
              'Component should be a component block'
            )
            currentValue = {
              label: node.value.title || 'Untitled',
              title: node.value.title,
              value: sourceBlockId,
              type: node.value.type,
              hidden: node.value.hidden,
              targetBlockId: node.value.typeOptions.targetBlockId,
              preview: component?.typeOptions.displayImage,
              breadcrumb,
              componentIndex: componentSourceBlockIds.indexOf(
                node.value.typeOptions.targetBlockId
              ),
              sourceBlockId,
            }
          } else {
            currentValue = {
              label: node.value.title || 'Untitled',
              title: node.value.title,
              value: sourceBlockId,
              type: node.value.type,
              preview: node.value.typeOptions.displayImage,
              hidden: node.value.hidden,
              componentIndex: componentSourceBlockIds.indexOf(sourceBlockId),
              sourceBlockId,
            }
          }

          const item = accumulator.find(
            (x) => x.value === currentValue.targetBlockId
          )
          let index = accumulator.indexOf(item as ReferenceOption)
          index = index !== -1 ? index + 1 : accumulator.length
          accumulator.splice(index, 0, currentValue)
          return accumulator
        },
        []
      ),
    [possibleReferenceNodes, getNode, componentSourceBlockIds]
  )

  const targetBlockIds = React.useMemo(
    () =>
      getValueBasedOnSelectedVariants({
        block,
        variantsToFilterBy: selectedDashboardVariants,
      }).targetBlockIds,
    [block, selectedDashboardVariants]
  )

  const rootBlockId = useWorkingTreeStore((s) => s.rootBlockId)

  const { data: loadReferencesResult } = useCustomSyncerSub(
    rootBlockId ? replicache : undefined,
    {
      syncer: 'loadReferences',
      params: {
        rootBlockId: rootBlockId ?? '',
        sourceBlockId: idToSourceBlockId(block),
        targetBlockIds,
      },
      keepPreviousData: true,
    }
  )

  const targetBlocks = useWorkingTreeStore(
    (s) => {
      const blockTreeManager = s.tree
      if (!blockTreeManager || !targetBlockIds.length) return []

      return targetBlockIds.reduce<Array<InstanceBlock | ComponentBlock>>(
        (acc, targetBlockId) => {
          const referencedBlock = blockTreeManager.getNode(targetBlockId, {
            fallbackKey: 'sourceBlockId',
          })
          if (!referencedBlock) {
            return acc.concat(getInvalidReferencedBlock(targetBlockId))
          }

          invariant(
            isInstanceBlock(referencedBlock.value) ||
              isComponentBlock(referencedBlock.value),
            `Referenced blocks should either be instances or components, found ${referencedBlock?.value.type}`
          )

          return acc.concat(referencedBlock.value)
        },
        []
      )
    },
    (prev, next) => {
      if (prev.length !== next.length) return false
      return prev.every(
        (p, i) => p.updatedAt === next[i]?.updatedAt && p.id === next[i]?.id
      )
    }
  )

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!targetBlocks[0] || !isSelected) {
        return
      }
      if (targetBlocks[0].id.startsWith('invalid-reference-')) return
      if (isHotkey('meta+shift+enter', e)) {
        e.preventDefault()
        e.stopPropagation()
        setSelectedBlockId(null)
        addTab({
          key: targetBlocks[0].id,
          partiallyOpen: true,
        })
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [targetBlocks, addTab, isSelected, setSelectedBlockId])

  const useMultipleReferenceInput = Boolean(
    block.constraints.multiple || targetBlocks.length > 1
  )
  const max = block.constraints.max
  const isDisabledByMax = Boolean(max && targetBlockIds.length >= max)

  const renderResult = (search: string) => {
    if (search) return null

    if (targetBlocks.length === 1 && !block.constraints.multiple) {
      const [targetBlock] = targetBlocks

      if (!targetBlock) return null

      if (targetBlock.id.startsWith('invalid-reference')) {
        return (
          <div
            className={clsx(
              'cursor-default flex items-center absolute space-x-2 leading-none pr-1.5',
              loadReferencesResult ? 'text-dark-error' : 'text-dark-muted'
            )}
          >
            <span className="relative">
              {loadReferencesResult ? (
                <ComponentNoneIcon />
              ) : (
                <Spinner width={15} height={15} />
              )}
            </span>
            <span>
              {loadReferencesResult
                ? targetBlock.title || 'Untitled'
                : 'Loading...'}
            </span>
          </div>
        )
      }

      return (
        <ButtonLink
          unstyled
          href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${idToSourceBlockId(
            targetBlock
          )}`}
          className="flex items-center absolute space-x-2 hover:underline leading-none pr-1.5"
          onClick={() => {
            addTab({ key: targetBlock.id, partiallyOpen: true })
            setFocus({
              type: 'block:document-title',
              blockId: idToSourceBlockId(targetBlock),
            })
          }}
        >
          <span className="relative">
            <BlockIcon block={targetBlock} parent={block} />
          </span>
          <span>{targetBlock.title || 'Untitled'}</span>
        </ButtonLink>
      )
    }

    return (
      <div className="absolute flex items-center space-x-2 leading-none text-dark-muted">
        <PlusIcon />
        <span>Reference or create a new instance</span>
      </div>
    )
  }

  const onSelect = React.useCallback(
    async (selectedOption: ReferenceOption) => {
      const referencedBlockId = selectedOption.value

      if (selectedOption.isNew) {
        invariant(
          selectedOption.type === blockTypesRecord.instance ||
            selectedOption.type === blockTypesRecord.component,
          'New option should either be an instance or component'
        )

        const dataForNewBlock =
          selectedOption.type === blockTypesRecord.instance
            ? {
                id: getId(),
                type: selectedOption.type,
                title: selectedOption.title || '',
                typeOptions: {
                  targetBlockId: selectedOption.value,
                },
              }
            : {
                id: getId(),
                type: selectedOption.type,
                title: selectedOption.title || '',
              }

        // disable this behavior cause it's a bit odd...
        // let placeNewBlockBelowPath: string | undefined
        // if (selectedOption.type === blockTypesRecord.instance) {
        //   const templateNode = getNode?.(selectedOption.value)
        //   const templateParentNode = templateNode?.parentKey
        //     ? getNode?.(templateNode.parentKey)
        //     : null
        //   if (templateParentNode && isListBlock(templateParentNode.value)) {
        //     placeNewBlockBelowPath =
        //       getPath?.(templateParentNode, {
        //         format: 'string',
        //       }) ?? undefined
        //   }
        // }

        const params: CreateAndReferenceBlockParams = {
          newBlock: {
            data: dataForNewBlock,
            path,
            isoNow: new Date().toISOString(),
            idSeed: getId(),
            parentInstanceChildTarget: block.instanceChildTarget,
            position: addNewBlockPosition
              ? {
                  targetId: addNewBlockPosition.id,
                  type: addNewBlockPosition.type,
                }
              : { type: 'last' },
          },
          referenceBlockInstanceChildTarget: block.instanceChildTarget,
          // placeNewBlockBelowPath,
          isoNow: new Date().toISOString(),
          referenceBlockId: block.id,
          multiple: useMultipleReferenceInput,
          path,
          idSeed: getId(),
          selectedDashboardVariants,
        }

        if (!block.constraints.startCollapsed) {
          localStorage.setItem(
            `nested-link:${dataForNewBlock.id}:expanded`,
            'true'
          )
        }

        const newBlockData =
          await replicache?.mutate.createAndReferenceBlock(params)

        if (newBlockData) {
          if (
            newBlockData.parentBlockSourceId !==
            (block.sourceBlockId || block.id)
          ) {
            const parentNode = getNode?.(newBlockData.parentBlockSourceId)
            invariant(
              parentNode,
              `parent with id ${newBlockData.parentBlockSourceId} was not found`
            )
            if (parentNode.value.type !== 'list') return
            toast(
              <div className="flex flex-col w-full gap-1.5">
                <p>
                  <Link
                    href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${newBlockData.newBlock.id}`}
                    onClick={() => {
                      addTab({
                        key: newBlockData.newBlock.id,
                        partiallyOpen: true,
                      })
                    }}
                    className="border-b"
                  >
                    New instance
                  </Link>{' '}
                  created under the{' '}
                  <Link
                    href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${newBlockData.parentBlockSourceId}`}
                    onClick={() => {
                      addTab({
                        key: newBlockData.parentBlockSourceId,
                        partiallyOpen: true,
                      })
                    }}
                    className="border-b"
                  >
                    "{parentNode.value.title}" collection
                  </Link>
                  .
                </p>
                {/* <code className="px-1 py-0.5 bg-dark-shade rounded-lg w-fit">
                  ⌘ ⇧ Enter
                </code> */}
              </div>
            )
          } else {
            // open in new tab
            addTab({ key: newBlockData.newBlock.id, partiallyOpen: true })
            const activeDocument = activeTabKey
              ? getNode?.(activeTabKey, {
                  fallbackKey: 'sourceBlockId',
                })
              : null
            setFocus({
              type: 'block:document-title',
              blockId: newBlockData.newBlock.id,
              temporaryBackLink: {
                title:
                  activeDocument?.value?.title ?? (block.title || 'Untitled'),
                id: activeDocument
                  ? idToSourceBlockId(activeDocument.value)
                  : sourceBlockId,
                edgeId:
                  activeDocument && activeDocument.value.id !== block.id
                    ? sourceBlockId
                    : undefined,
              },
            })
          }

          if (newBlockData.undo) {
            undoManager.current?.push({
              redo: [
                focusValueOp,
                { op: 'createAndReferenceBlock', args: params },
              ],
              undo: [
                focusValueOp,
                {
                  op: 'blockBatchMutations',
                  args: { mutations: newBlockData.undo },
                },
              ],
            })
          }
        }

        return
      }

      let newTargetBlocksId: ReferenceBlock['value']['targetBlockIds']
      if (!useMultipleReferenceInput) {
        newTargetBlocksId = [referencedBlockId]
      } else if (targetBlockIds.includes(referencedBlockId)) {
        newTargetBlocksId = targetBlockIds.filter(
          (blockId) => blockId !== referencedBlockId
        )
      } else {
        if (!addNewBlockPosition) {
          newTargetBlocksId = [...targetBlockIds, referencedBlockId]
        } else {
          newTargetBlocksId = insertIntoArray({
            array: targetBlockIds,
            item: referencedBlockId,
            getItemId: (id) => {
              // pass it to regular (non-source) id
              return getNode?.(id)?.value.id ?? id
            },
            position: {
              targetId: addNewBlockPosition.id,
              type: addNewBlockPosition.type,
            },
          })
        }
      }

      const hasVariantsSelected = selectedDashboardVariants.length > 0
      const params: UpdateBlockParams = {
        id: block.id,
        instanceChildTarget: block.instanceChildTarget,
        data: getBlockUpdateOverride({
          block,
          newValue: {
            targetBlockIds: newTargetBlocksId,
          },
          selectedVariants: selectedDashboardVariants,
        }),
        strategy: hasVariantsSelected ? 'merge-deep' : 'merge',
        path,
        isoNow: new Date().toISOString(),
      }

      const result = await replicache?.mutate.updateBlock(params)
      if (result?.undo) {
        undoManager.current?.push({
          redo: [focusValueOp, { op: 'updateBlock', args: params }],
          undo: [
            focusValueOp,
            { op: 'blockBatchMutations', args: { mutations: result.undo } },
          ],
        })
      }
    },
    [
      useMultipleReferenceInput,
      block,
      targetBlockIds,
      selectedDashboardVariants,
      path,
      replicache?.mutate,
      addNewBlockPosition,
      getNode,
      teamSlug,
      repoSlug,
      encodedSegment,
      addTab,
      activeTabKey,
      setFocus,
      sourceBlockId,
      undoManager,
      focusValueOp,
    ]
  )

  const onClear = React.useCallback(async () => {
    const params: UpdateBlockParams = {
      id: block.id,
      instanceChildTarget: block.instanceChildTarget,
      data: {
        type: blockTypesRecord.reference,
        value: { targetBlockIds: [] },
      },
      strategy: 'merge-deep',
      path,
      isoNow: new Date().toISOString(),
    }
    const result = await replicache?.mutate.updateBlock(params)
    if (result?.undo) {
      undoManager.current?.push({
        redo: [focusValueOp, { op: 'updateBlock', args: params }],
        undo: [
          focusValueOp,
          { op: 'blockBatchMutations', args: { mutations: result.undo } },
        ],
      })
    }
  }, [
    block.id,
    block.instanceChildTarget,
    focusValueOp,
    path,
    replicache?.mutate,
    undoManager,
  ])

  const singleTargetBlockId = !useMultipleReferenceInput
    ? (targetBlockIds[0] ?? null)
    : null
  const singleTargetNode = singleTargetBlockId
    ? (getNode?.(singleTargetBlockId) ?? null)
    : null
  const singleTargetParentNode = singleTargetNode?.parentKey
    ? getNode?.(singleTargetNode.parentKey)
    : null
  const singleTargetExpanded = useExpandedState(block)

  const onRemoveTarget = React.useCallback(
    async (id: string) => {
      id = id.replace('invalid-reference-', '')
      const params: UpdateBlockParams = {
        id: block.id,
        instanceChildTarget: block.instanceChildTarget,
        data: getBlockUpdateOverride({
          block,
          selectedVariants: selectedDashboardVariants,
          newValue: {
            targetBlockIds: targetBlockIds.filter((tId) => tId !== id),
          },
        }),
        strategy: 'merge-deep',
        path,
        isoNow: new Date().toISOString(),
      }
      const result = await replicache?.mutate.updateBlock(params)
      if (result?.undo) {
        undoManager.current?.push({
          redo: [focusValueOp, { op: 'updateBlock', args: params }],
          undo: [
            focusValueOp,
            { op: 'blockBatchMutations', args: { mutations: result.undo } },
          ],
        })
      }
    },
    [
      block,
      selectedDashboardVariants,
      targetBlockIds,
      path,
      replicache?.mutate,
      undoManager,
      focusValueOp,
    ]
  )

  React.useEffect(() => {
    if (!containerRef.current) return
    const container = containerRef.current

    const onDeleteEvent = (e: Event) => {
      if (!getNode) return
      if (e instanceof CustomEvent) {
        const { selectedBlockId } = e.detail as { selectedBlockId: string }
        onRemoveTarget(
          idToSourceBlockId(getNode(selectedBlockId, { required: true }).value)
        )
      }
    }

    container.addEventListener('delete-event', onDeleteEvent)
    return () => {
      container.removeEventListener('delete-event', onDeleteEvent)
    }
  }, [getNode, onRemoveTarget])

  const onRemoveTargetRef = React.useRef(onRemoveTarget)
  // eslint-disable-next-line react-compiler/react-compiler
  onRemoveTargetRef.current = onRemoveTarget

  const processedOptions = React.useMemo(() => {
    // 1: Create a place to store the last component viewed in the iteration process
    let lastComponent: ReferenceOption | null = null
    const instancesLoadedByComponent = new Map<string, number>()

    // 2: Iterate throughout options
    const filteredOptions = options.reduce<Array<ReferenceOption>>(
      (acum, current) => {
        // 3: If this is a comp, we override the last viewed comp
        const node = getNode?.(current.value)
        if (
          current.type === blockTypesRecord.component &&
          !block.constraints.disableCreateNew
        ) {
          invariant(node, `node with id ${current.value} was not found`)
          const typeOption = findAcceptedTypeOptionForBlock({
            refBlock: block,
            evaluatedBlock: node.value,
            typeOptions,
          })
          lastComponent = {
            title: '',
            label: `New ${typeOption?.label || 'Untitled'}`,
            value: current.value,
            type: blockTypesRecord.instance,
            keywords: [typeOption?.label || ''],
            preview:
              node.value.type === 'component'
                ? node.value.typeOptions.displayImage
                : undefined,
            isNew: true,
            componentIndex: current.componentIndex,
            sourceBlockId: idToSourceBlockId(node.value),
          }
        }

        if (
          // 3: If this is not hidden and if its not already added in a multi option reference selector, we add it
          !block.constraints.disableLinkExisting &&
          !current.hidden
        ) {
          const targetBlockId = current.targetBlockId
          if (targetBlockId) {
            instancesLoadedByComponent.set(
              targetBlockId,
              (instancesLoadedByComponent.get(targetBlockId) ?? 0) + 1
            )
          }

          acum.push(current)
        }

        // 4: If we are about to fall in a separator, we add the last viewed comp
        if (lastComponent) {
          acum.push(lastComponent)
          const hasRecordedInstances = instancesLoadedByComponent.get(
            lastComponent.sourceBlockId
          )
          if (hasRecordedInstances === undefined) {
            instancesLoadedByComponent.set(lastComponent.sourceBlockId, 0)
          }
          lastComponent = null
        }

        return acum
      },
      []
    )

    const sorted = [...filteredOptions].sort((a, b) => {
      if (b.value === a.targetBlockId) {
        if (b.isNew) return -1
        if (b.loading) return 0
        else return 1
      }

      if (a.value === b.targetBlockId) {
        if (a.isNew) return 1
        if (a.loading) return 0
        else return -1
      }

      const sort = compareInstanceSearchQueueItem(
        {
          sourceBlockId: a.sourceBlockId,
          componentIndex: a.componentIndex,
          title: a.title || '',
        },
        {
          sourceBlockId: b.sourceBlockId,
          componentIndex: b.componentIndex,
          title: b.title || '',
        }
      )

      return sort
    })

    return [...sorted].flatMap((option) => {
      if (!option.isNew || !instancesPerComponent) return [option]

      const targetBlockId = option.targetBlockId ?? option.value
      const totalInstances = instancesPerComponent[targetBlockId] ?? 0
      const loadedInstances = instancesLoadedByComponent.get(targetBlockId) ?? 0
      const hasLoadedAll = loadedInstances >= totalInstances
      if (hasLoadedAll) {
        return [option]
      }

      const missingItems: ReferenceOption[] = Array.from<ReferenceOption>({
        length: totalInstances - loadedInstances,
      }).fill({
        title: 'Loading...',
        value: targetBlockId,
        label: 'Loading...',
        sourceBlockId: targetBlockId,
        type: blockTypesRecord.instance,
        componentIndex: 0,
        targetBlockId,
        loading: true,
        isNew: false,
      })

      return [...missingItems, option]
    })
  }, [block, getNode, instancesPerComponent, options, typeOptions])

  const getOptions = React.useCallback(
    () => processedOptions,
    [processedOptions]
  )

  React.useEffect(() => {
    // Compute rowMeta based on processed options
    processedOptions.forEach((option, index) => {
      const previousOption = processedOptions[index - 1]
      const previousOptionTargetBlock =
        previousOption?.targetBlockId ?? previousOption?.value
      const optionTargetBlock = option.targetBlockId ?? option.value
      const shouldRenderTypeOption =
        optionTargetBlock !== previousOptionTargetBlock
      const optionNode = getNode?.(option.value)
      const typeOption =
        shouldRenderTypeOption && optionNode?.value
          ? findAcceptedTypeOptionForBlock({
              refBlock: block,
              evaluatedBlock: optionNode.value,
              typeOptions,
            })
          : null
      rowsMeta.current.set(index, { shouldRenderTypeOption, typeOption })
    })
  }, [block, getNode, processedOptions, rowsMeta, typeOptions, virtualizer])

  const createNewActions = React.useMemo((): MenuItem[] => {
    return acceptedTypeOptions
      .map((typeOption) => {
        return {
          key: `create-${typeOption.id}`,
          type: 'action' as const,
          icon: <BlockIcon block={{ type: typeOption.type }} />,
          label: typeOption.label,
          onSelect: async () => {
            // Create a virtual option similar to the one in the attached code
            const virtualOption: ReferenceOption = {
              title: '',
              label: `New ${typeOption.label}`,
              value: typeOption.id,
              type: blockTypesRecord.instance,
              keywords: [typeOption.label],
              preview: typeOption.preview,
              isNew: true,
              componentIndex: 0,
              sourceBlockId: typeOption.id,
            }

            // Use the existing onSelect function
            await onSelect(virtualOption)
          },
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [acceptedTypeOptions, onSelect])

  return (
    <>
      <div
        className={clsx(
          useMultipleReferenceInput &&
            'border border-dark-border-solid border-dashed rounded-lg p-2 bg-dark-bg'
        )}
        ref={containerRef}
        data-capture-delete-event
      >
        {useMultipleReferenceInput && (
          <ReferenceInputStoreContext.Provider value={store}>
            <Blocks
              block={block}
              path={path}
              variant="nested-union"
              selectedDashboardVariants={selectedDashboardVariants}
              isLoadingReferenceTargets={!loadReferencesResult}
            />
          </ReferenceInputStoreContext.Provider>
        )}
        <Portal
          disablePortal={!addNewBlockPosition}
          id={`union-wrapper-portal-${
            addNewBlockPosition?.type === 'before' ? 'top' : 'bottom'
          }-${addNewBlockPosition?.id}`}
        >
          {!isDisabledByMax && (
            <div className={clsx(useMultipleReferenceInput && 'p-2')}>
              <Selector
                sideOffset={12}
                extraWidth={20}
                id={`${idToSourceBlockId(block)}-reference-input`}
                className="!rounded-md"
                disabled={isReadOnly || isDisabledByMax}
                readOnly={isReadOnly}
                autoFocus={!!addNewBlockPosition}
                onFocus={() => setSelectedBlockId(idToSourceBlockId(block))}
                onPopoverOpen={() => {
                  setIsPopoverOpen(true)
                }}
                onPopoverClose={() => {
                  setAddNewBlockPosition(null)
                  setIsPopoverOpen(false)
                }}
                showCreateNew={!isDisabledByMax}
                options={getOptions}
                closeAfterSelect={!useMultipleReferenceInput}
                onSelect={onSelect}
                onKeyDown={async (e) => {
                  if (
                    targetBlockIds.length <= 1 &&
                    !useMultipleReferenceInput
                  ) {
                    return
                  }
                  if (!e.target) return
                  const input = e.target as HTMLInputElement
                  if (input.value) return
                  if (e.key !== 'Backspace' && !isHotkey('meta+backspace', e)) {
                    return
                  }

                  const newValue = isHotkey('meta+backspace', e)
                    ? []
                    : [...targetBlockIds]
                  newValue.pop()

                  const params: UpdateBlockParams = {
                    id: block.id,
                    instanceChildTarget: block.instanceChildTarget,
                    data: getBlockUpdateOverride({
                      selectedVariants: selectedDashboardVariants,
                      block,
                      newValue: { targetBlockIds: newValue },
                    }),
                    strategy: 'merge-deep',
                    path,
                    isoNow: new Date().toISOString(),
                  }
                  const result = await replicache?.mutate.updateBlock(params)

                  if (result?.undo) {
                    undoManager.current?.push({
                      redo: [focusValueOp, { op: 'updateBlock', args: params }],
                      undo: [
                        focusValueOp,
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: result.undo },
                        },
                      ],
                    })
                  }
                }}
                onRemove={
                  targetBlockIds.length && !useMultipleReferenceInput
                    ? onClear
                    : undefined
                }
                renderResult={renderResult}
                expandableMode={{
                  toggle: () => {
                    singleTargetExpanded.setExpanded(
                      !singleTargetExpanded.expanded
                    )
                  },
                  children:
                    singleTargetExpanded.expanded &&
                    singleTargetNode &&
                    (isInstanceBlock(singleTargetNode.value) ||
                      isComponentBlock(singleTargetNode.value)) &&
                    singleTargetParentNode &&
                    getPath ? (
                      <div>
                        <ChildBlocksExpanded
                          block={singleTargetNode.value}
                          parentBlock={block}
                          pathForNestedBlocks={getPath(singleTargetNode, {
                            required: true,
                          })}
                        />
                      </div>
                    ) : null,
                }}
                icon={
                  <span className="size-6 flex items-center justify-center">
                    {singleTargetNode &&
                      (singleTargetExpanded.expanded ? (
                        <ChevronUpIcon className="size-4" />
                      ) : (
                        <ChevronDownIcon className="size-4" />
                      ))}
                  </span>
                }
                search={search}
                setSearch={setSearch}
              >
                {({
                  options,
                  getOptionProps,
                  selectedIndex,
                  listRef,
                  scrollerElement,
                }) => {
                  scrollableElementRef.current = scrollerElement

                  if (!options.length) {
                    return (
                      <SelectorEmptyState>
                        Nothing to reference.
                      </SelectorEmptyState>
                    )
                  }

                  if (
                    !instanceSearchResult &&
                    virtualizer.getVirtualItems().length === 0
                  ) {
                    return <SelectorEmptyState>Loading...</SelectorEmptyState>
                  }

                  return (
                    <ReferenceOptionsVirtualizedList
                      virtualizer={virtualizer}
                      options={options}
                      getOptionProps={getOptionProps}
                      selectedIndex={selectedIndex}
                      listRef={listRef}
                      block={block}
                      getNode={getNode}
                      targetBlocks={targetBlocks}
                      rowsMeta={rowsMeta.current}
                      hasSearch={Boolean(search)}
                    />
                  )
                }}
              </Selector>
            </div>
          )}
        </Portal>

        <BlockLabelMoreActionsPortal blockId={block.id}>
          {!block.constraints.disableCreateNew && (
            <div className="mr-3">
              <SimpleDropdownMenu
                actions={createNewActions}
                contentProps={{ className: 'max-h-64 !overflow-auto' }}
              >
                <Button
                  icon={<ChevronDownIcon />}
                  iconSide="right"
                  size="small"
                >
                  Add New
                </Button>
              </SimpleDropdownMenu>
            </div>
          )}
          {useMultipleReferenceInput && (
            <ExpandCollapseAll block={block} targetBlockIds={targetBlockIds} />
          )}
        </BlockLabelMoreActionsPortal>
      </div>
    </>
  )
}

const ReferenceOptionsVirtualizedList = React.memo(
  ({
    virtualizer,
    options,
    getOptionProps,
    selectedIndex,
    listRef,
    block,
    getNode,
    targetBlocks,
    rowsMeta,
    hasSearch,
  }: {
    virtualizer: Virtualizer<HTMLDivElement, Element>
    options: ReferenceOption[]
    getOptionProps: (index: number) => Record<string, unknown>
    selectedIndex: number | undefined
    listRef: React.RefObject<null | HTMLDivElement>
    block: ReferenceBlock
    getNode: TreeManager<Block, NonNullable<unknown>>['getNode'] | undefined
    targetBlocks: (InstanceBlock | ComponentBlock)[]
    rowsMeta: RowsMeta
    hasSearch: boolean
  }) => {
    const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)
    const childrenKeys = React.useMemo(
      () => getChildrenKeys?.(block.sourceBlockId || block.id) ?? [],
      [getChildrenKeys, block.sourceBlockId, block.id]
    )

    React.useEffect(() => {
      virtualizer.measure()
    }, [options.length, virtualizer])

    const actualHeight = React.useMemo(() => {
      const totalSize = virtualizer.getTotalSize()
      const totalCount = virtualizer.options.count

      // When we have filtered options that are fewer than total count
      if (options.length < totalCount && options.length > 0) {
        // Calculate height based on options.length instead of virtualizer items
        let height = 0
        for (let i = 0; i < options.length; i++) {
          const meta = rowsMeta.get(i)
          if (meta?.shouldRenderTypeOption && meta.typeOption) {
            height += i === 0 ? 56 : 64
          } else {
            height += 32
          }
        }
        return height
      }

      return totalSize
    }, [virtualizer, options.length, rowsMeta])

    return (
      <div
        style={{
          height: `${hasSearch ? actualHeight : virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const index = virtualItem.index
          const option = options[index]
          const meta = rowsMeta.get(index)
          const shouldRenderTypeOption = meta?.shouldRenderTypeOption ?? false
          const typeOption = meta?.typeOption ?? null

          if (!option) return null

          if (option.loading) {
            return (
              <div
                key={virtualItem.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {shouldRenderTypeOption && (
                  <>
                    {index !== 0 && (
                      <hr className="my-1 border-dark-border-solid" />
                    )}
                    {typeOption && (
                      <span className="ml-2 text-dark-faint text-xs font-medium">
                        {typeOption.label}
                      </span>
                    )}
                  </>
                )}
                <div className="flex items-center justify-normal px-2.5 gap-1 h-8">
                  <Skeleton
                    height={20}
                    width={20}
                    borderless
                    className="shrink-0"
                  />
                  <Skeleton height={16} width={220} borderless />
                  <Skeleton height={16} width={100} borderless />
                </div>
              </div>
            )
          }

          const isHighlighted = selectedIndex === index
          const isSelected =
            targetBlocks.some(
              (block) => idToSourceBlockId(block) === option.value
            ) && !option.isNew
          const optionNode = getNode?.(option.value)

          return (
            <div
              key={virtualItem.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <div>
                {shouldRenderTypeOption && (
                  <>
                    {index !== 0 && (
                      <hr className="my-1 border-dark-border-solid" />
                    )}
                    {typeOption && (
                      <span className="ml-2 text-dark-faint text-xs font-medium">
                        {typeOption.label}
                      </span>
                    )}
                  </>
                )}
                <SelectorItem
                  {...getOptionProps(index)}
                  className={clsx(
                    isHighlighted && 'bg-dark-shade outline-none',
                    option.isNew && 'text-dark-control-highlight',
                    'scroll-mt-1 relative flex h-8 cursor-default select-none items-center rounded px-3 py-1.5 text-xs leading-none outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                  )}
                >
                  <span className="relative">
                    <BlockIcon
                      block={{ type: option.type }}
                      forceReferenceLinkIcon={
                        !option.isNew &&
                        childrenKeys.some(
                          (bId) => bId === optionNode?.value.id
                        ) === false
                      }
                    />
                    {option.isNew && (
                      <span className="absolute -bottom-0.5 -right-1">
                        <PlusIcon
                          className="size-3"
                          borderColor={
                            isHighlighted
                              ? 'var(--color-shade-hover)'
                              : 'var(--color-shade)'
                          }
                        />
                      </span>
                    )}
                  </span>
                  <span className="inline-flex items-center justify-between gap-x-2 w-full ml-2 max-w-full overflow-hidden whitespace-nowrap">
                    <span className="overflow-hidden text-ellipsis">
                      {option.label}
                    </span>
                    {!!option.breadcrumb?.length && (
                      <span
                        className="text-dark-muted flex-1 min-w-min overflow-hidden text-ellipsis whitespace-nowrap"
                        title={option.breadcrumb.join('/')}
                      >
                        {option.breadcrumb.join('/')}
                      </span>
                    )}
                  </span>

                  {isSelected && (
                    <span className="text-dark-control-highlight text-xs font-medium inline-flex items-center">
                      <CheckIcon className="size-4 mr-0.5" />
                      Selected
                    </span>
                  )}
                </SelectorItem>
              </div>

              {option.preview && isHighlighted && (
                <OptionPreview
                  option={option}
                  containerRef={listRef}
                  isHighlighted={isHighlighted}
                />
              )}
            </div>
          )
        })}
      </div>
    )
  }
)

const OptionPreview = React.memo(
  ({
    option,
    containerRef,
    isHighlighted,
  }: {
    option: ReferenceOption
    containerRef: React.RefObject<null | HTMLElement>
    isHighlighted: boolean
  }) => {
    const isOn = !!option.preview && isHighlighted

    const { refs, floatingStyles } = useFloating({
      placement: 'left',
      open: isOn,
      middleware: [
        inline(),
        offset({ mainAxis: 12 }),
        autoPlacement({
          alignment: 'start',
          autoAlignment: true,
          padding: 20,
          allowedPlacements: [
            'left-start',
            'left-end',
            'right-start',
            'right-end',
          ],
        }),
      ],
      whileElementsMounted: autoUpdate,
      elements: {
        // eslint-disable-next-line react-compiler/react-compiler
        reference: containerRef.current,
      },
    })

    if (!option.preview || !isHighlighted) return null

    return (
      <FloatingPortal>
        <div
          ref={refs.setFloating}
          className="rounded-xl overflow-hidden border border-dark-border-solid bg-dark-shade shadow-[0px_14px_16px_rgba(0,0,0,0.3)] flex items-center right-[calc(100%+8px)] w-[324px] z-[1000]"
          style={{
            ...floatingStyles,
            maxWidth: `calc((100vw - ${containerRef.current?.offsetWidth}px) / 2)`,
          }}
        >
          <>
            {VALID_IMAGE_MIME_TYPES.includes(option.preview.mimeType) && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={option.preview.url}
                alt=""
                className="w-full inset-0 object-cover"
                style={{
                  maxHeight: 290,
                  aspectRatio: `${option.preview.width}/${option.preview.height}`,
                }}
              />
            )}
            {VALID_VIDEO_MIME_TYPES.includes(option.preview.mimeType) && (
              <video
                src={option.preview.url}
                className="w-full inset-0 object-cover"
                style={{
                  maxHeight: 290,
                  aspectRatio: `${option.preview.width}/${option.preview.height}`,
                }}
                autoPlay
                muted
                playsInline
                loop
              />
            )}
          </>
        </div>
      </FloatingPortal>
    )
  }
)

export const ArrowTopRightSmallIcon = ({ className = '' }) => {
  const id = React.useId()

  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask
        id={id}
        maskUnits="userSpaceOnUse"
        x="5"
        y="5"
        width="11"
        height="11"
        fill="black"
      >
        <rect fill="white" x="5" y="5" width="11" height="11" />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M6.16206 13.8899C5.94674 13.6757 5.94587 13.3274 6.16012 13.1121L12.1422 7.10009L8.69842 7.1C8.39467 7.09999 8.14843 6.85374 8.14844 6.54999C8.14845 6.24623 8.3947 5.99999 8.69845 6L13.4653 6.00013C13.6112 6.00013 13.7511 6.0581 13.8543 6.16128C13.9574 6.26446 14.0153 6.40439 14.0153 6.55029L14.0139 11.3177C14.0138 11.6215 13.7675 11.8676 13.4638 11.8675C13.16 11.8675 12.9138 11.6211 12.9139 11.3174L12.9149 7.88303L6.93988 13.888C6.72563 14.1033 6.37739 14.1042 6.16206 13.8899Z"
        />
      </mask>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.16206 13.8899C5.94674 13.6757 5.94587 13.3274 6.16012 13.1121L12.1422 7.10009L8.69842 7.1C8.39467 7.09999 8.14843 6.85374 8.14844 6.54999C8.14845 6.24623 8.3947 5.99999 8.69845 6L13.4653 6.00013C13.6112 6.00013 13.7511 6.0581 13.8543 6.16128C13.9574 6.26446 14.0153 6.40439 14.0153 6.55029L14.0139 11.3177C14.0138 11.6215 13.7675 11.8676 13.4638 11.8675C13.16 11.8675 12.9138 11.6211 12.9139 11.3174L12.9149 7.88303L6.93988 13.888C6.72563 14.1033 6.37739 14.1042 6.16206 13.8899Z"
        fill="var(--color-bright)"
      />
      <path
        d="M6.16012 13.1121L6.86899 13.8174L6.86899 13.8174L6.16012 13.1121ZM12.1422 7.10009L12.851 7.80543L14.5478 6.10016L12.1422 6.10009L12.1422 7.10009ZM8.69842 7.1L8.69845 6.1L8.69845 6.1L8.69842 7.1ZM8.69845 6L8.69848 5L8.69848 5L8.69845 6ZM13.4653 6.00013L13.4653 7.00013L13.4653 7.00013L13.4653 6.00013ZM13.8543 6.16128L13.147 6.86828L13.8543 6.16128ZM14.0153 6.55029L15.0153 6.55057L15.0153 6.55057L14.0153 6.55029ZM14.0139 11.3177L15.0139 11.318L15.0139 11.318L14.0139 11.3177ZM13.4638 11.8675L13.4641 10.8675L13.4641 10.8675L13.4638 11.8675ZM12.9139 11.3174L11.9139 11.3171L11.9139 11.3171L12.9139 11.3174ZM12.9149 7.88303L13.9149 7.88331L13.9156 5.45958L12.206 7.17769L12.9149 7.88303ZM6.93988 13.888L6.23101 13.1826L6.23101 13.1826L6.93988 13.888ZM5.45125 12.4068C4.84745 13.0136 4.8499 13.995 5.45672 14.5988L6.8674 13.181C7.04358 13.3563 7.04429 13.6413 6.86899 13.8174L5.45125 12.4068ZM11.4333 6.39475L5.45125 12.4068L6.86899 13.8174L12.851 7.80543L11.4333 6.39475ZM8.6984 8.1L12.1421 8.10009L12.1422 6.10009L8.69845 6.1L8.6984 8.1ZM7.14844 6.54996C7.14841 7.406 7.84235 8.09998 8.6984 8.1L8.69845 6.1C8.94698 6.10001 9.14844 6.30148 9.14844 6.55001L7.14844 6.54996ZM8.69848 5C7.84244 4.99998 7.14846 5.69392 7.14844 6.54996L9.14844 6.55001C9.14843 6.79854 8.94695 7.00001 8.69843 7L8.69848 5ZM13.4653 5.00013L8.69848 5L8.69843 7L13.4653 7.00013L13.4653 5.00013ZM14.5615 5.45429C14.2708 5.16351 13.8765 5.00014 13.4653 5.00013L13.4653 7.00013C13.3459 7.00013 13.2314 6.9527 13.147 6.86828L14.5615 5.45429ZM15.0153 6.55057C15.0154 6.13941 14.8522 5.74506 14.5615 5.45429L13.147 6.86828C13.0626 6.78386 13.0152 6.66937 13.0153 6.55L15.0153 6.55057ZM15.0139 11.318L15.0153 6.55057L13.0153 6.55L13.0139 11.3174L15.0139 11.318ZM13.4635 12.8675C14.3195 12.8678 15.0137 12.174 15.0139 11.318L13.0139 11.3174C13.014 11.0689 13.2155 10.8675 13.4641 10.8675L13.4635 12.8675ZM11.9139 11.3171C11.9137 12.1731 12.6075 12.8673 13.4635 12.8675L13.4641 10.8675C13.7126 10.8676 13.914 11.0691 13.9139 11.3177L11.9139 11.3171ZM11.9149 7.88275L11.9139 11.3171L13.9139 11.3177L13.9149 7.88331L11.9149 7.88275ZM7.64875 14.5933L13.6238 8.58837L12.206 7.17769L6.23101 13.1826L7.64875 14.5933ZM5.45672 14.5988C6.06355 15.2026 7.04495 15.2001 7.64875 14.5933L6.23101 13.1826C6.40631 13.0064 6.69123 13.0057 6.8674 13.181L5.45672 14.5988Z"
        fill="var(--color-shade)"
        mask={`url(#${id})`}
      />
    </svg>
  )
}

export const PlusIcon = ({
  className = '',
  borderColor = 'var(--color-shade)',
}) => {
  return (
    <svg
      className={className}
      viewBox="0 0 15 15"
      width="15"
      height="15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.5 2.75a1 1 0 0 0-2 0V6.5H2.75a1 1 0 0 0 0 2H6.5v3.75a1 1 0 0 0 2 0V8.5h3.75a1 1 0 0 0 0-2H8.5V2.75Z"
        fill="currentColor"
        stroke={borderColor}
      />
    </svg>
  )
}

const ExpandCollapseAll = ({
  block,
  targetBlockIds,
}: {
  block: ReferenceBlock
  targetBlockIds: string[]
}) => {
  const [scheduleExpandAll, setScheduleExpandAll] = React.useState(false)
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const { setMultiple } = useExpandedState(block)

  React.useEffect(() => {
    if (!scheduleExpandAll) return
    const children = targetBlockIds
      .map((id) => getNode?.(id)?.value ?? null)
      .filter(Boolean)
    /** Do the work in batches to avoid React's NESTED_UPDATE_LIMIT */
    const batchSize = 8
    const batches = children.reduce<Block[][]>(
      (acum, child) => {
        if (acum[acum.length - 1]?.length === batchSize) {
          acum.push([])
        }
        acum[acum.length - 1]?.push(child)
        return acum
      },
      [[]]
    )

    let i = 0

    function processBatch() {
      const batch = batches[i]
      if (!batch) {
        // all done
        clearInterval(interval)
        setScheduleExpandAll(false)
        return
      }
      setMultiple(batch, true)
      i++
    }

    const interval = setInterval(() => {
      processBatch()
    }, 250)

    processBatch()

    return () => {
      clearInterval(interval)
    }
  }, [targetBlockIds, getNode, scheduleExpandAll, setMultiple])

  return (
    <>
      <SimpleTooltip content="Expand all">
        <Button
          unstyled
          tabIndex={-1}
          focusStyled
          onClick={() => {
            setScheduleExpandAll(true)
          }}
          className="mr-2 flex cursor-default items-center space-x-1 text-[13px] leading-4 text-dark-muted !outline-dark-border-solid hover:text-dark-dim"
        >
          <ExpandIcon width={16} height={16} />
        </Button>
      </SimpleTooltip>
      <SimpleTooltip content="Collapse all">
        <Button
          unstyled
          tabIndex={-1}
          focusStyled
          onClick={() => {
            setScheduleExpandAll(false)
            setMultiple(
              targetBlockIds
                .map((id) => getNode?.(id)?.value ?? null)
                .filter(Boolean),
              false
            )
          }}
          className="mr-2 flex cursor-default items-center space-x-1 text-[13px] leading-4 text-dark-muted !outline-dark-border-solid hover:text-dark-dim"
        >
          <CollapseIcon width={16} height={16} />
        </Button>
      </SimpleTooltip>
    </>
  )
}
